/**
 * @file ChatMessage.jsx
 * @description A component responsible for rendering a single message within the chat conversation.
 *
 * @component MemoizedChatMessage
 * @props {object} message - The message object containing its content, role, and status.
 *
 * @imports
 * - React (memo): To optimize performance by preventing re-renders of unchanged messages.
 * - ReactMarkdown, remark-gfm, rehype-highlight: To render the assistant's response with rich formatting, including tables, lists, and syntax-highlighted code.
 * - Lucide-react (FileText): For file attachment icons.
 * - styles (from ChatApp.module.css and FileUpload.module.css): For styling the message bubbles and file chips.
 *
 * @purpose This component handles the visual representation of a message. It applies different styles based on the message's `role` ('user', 'assistant', or 'system'). For assistant messages, it uses `ReactMarkdown` to interpret and display formatted content. It also displays file attachments for user messages. It is wrapped in `React.memo` for a significant performance boost in long conversations.
 */

import React, { memo, useState } from 'react';
import { FileText, FileCode, FileJson, Table, Image, File, Globe, ChevronDown } from 'lucide-react';
import styles from '../../styles/ChatApp.module.css';
import fileStyles from '../../styles/FileUpload.module.css';
import MarkdownRenderer from './MarkdownRenderer';
import { parseThinkingContent } from '../common/thinkingParser';

/**
 * Helper function to get the appropriate icon for a file type
 */
const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop().toLowerCase();
    
    // Code files
    if (['py', 'js', 'jsx', 'java', 'cpp', 'html', 'css', 'md', 'tsx', 'ts'].includes(ext)) {
        return <FileCode size={14} />;
    }
    // Data files
    if (['json', 'xml'].includes(ext)) {
        return <FileJson size={14} />;
    }
    // Table files
    if (['csv', 'tsv', 'xls', 'xlsx'].includes(ext)) {
        return <Table size={14} />;
    }
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
        return <Image size={14} />;
    }
    // PDF and documents
    if (['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext)) {
        return <FileText size={14} />;
    }
    // Default file icon
    return <File size={14} />;
};

/**
 * Helper function to format file size
 */
const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Collapsible block that shows the model's chain-of-thought / reasoning content.
 */
const ThinkingBlock = ({ blocks }) => {
    const [isOpen, setIsOpen] = useState(false);
    if (!blocks || blocks.length === 0) return null;
    return (
        <div className={styles.thinkingBlock}>
            <button
                type="button"
                className={styles.thinkingToggle}
                onClick={() => setIsOpen(prev => !prev)}
                aria-expanded={isOpen}
            >
                <ChevronDown
                    size={13}
                    className={`${styles.thinkingChevron} ${isOpen ? styles.thinkingChevronOpen : ''}`}
                />
                <span>Thinking</span>
            </button>
            {isOpen && (
                <div className={styles.thinkingContent}>
                    {blocks.map((block, i) => (
                        <MarkdownRenderer key={i} content={block} />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Displays a single message in the chat history.
 * It handles different styles for user, assistant, and system messages.
 *
 * @param {object} props
 * @param {object} props.message - The message object to display.
 * @param {string} props.message.role - 'user', 'assistant', or 'system'.
 * @param {string} props.message.content - The text content of the message.
 * @param {boolean} [props.message.isStreaming] - Flag for showing the streaming cursor.
 * @param {string} [props.message.modelUsed] - The AI model that generated the message.
 * @param {Array} [props.message.files] - Array of file attachments.
 */
const ChatMessage = ({ message }) => {
    const { role, content, isStreaming, modelUsed, files, webSearchEnabled } = message;
    const isUser = role === 'user';

    // Parse out any reasoning/thinking blocks from assistant messages.
    const { content: visibleContent, thinkingBlocks } = isUser
        ? { content, thinkingBlocks: [] }
        : parseThinkingContent(content);

    // System messages have a unique, simple layout.
    if (role === 'system') {
        return <div className={styles.systemMessage}>{content}</div>;
    }

    // Determine the appropriate CSS classes based on the message role.
    const wrapperClass = isUser ? `${styles.messageWrapper} ${styles.user}` : `${styles.messageWrapper} ${styles.assistant}`;
    const bubbleClass = isUser ? `${styles.messageBubble} ${styles.userBubble}` : `${styles.messageBubble} ${styles.assistantBubble}`;

    return (
        <div
            className={wrapperClass}
            {...(isUser && {
                'data-user-msg': 'true',
                'data-preview': content.slice(0, 60),
            })}
        >
            {/* Display header info for assistant messages. */}
            {!isUser && modelUsed && (
                <span className={styles.modelInfo}>
                    {modelUsed && `Answer from: ${modelUsed}`}
                    
                    {/* The web search indicator is now just another element INSIDE the original span */}
                    {webSearchEnabled && (
                        <span className={styles.webSearchIndicator}>
                            <Globe size={12} /> Web Search
                        </span>
                    )}
                </span>
            )}

            <div className={bubbleClass}>
                {isUser ? (
                    <>
                        {/* User messages are plain text, wrapped in a <p> tag for consistent styling. */}
                        <p>{content}</p>
                        {/* Display attached files if any */}
                        {files && files.length > 0 && (
                            <div className={fileStyles.messageFiles}>
                                {files.map((file, index) => (
                                    <div key={index} className={fileStyles.messageFileChip}>
                                        {getFileIcon(file.name)}
                                        <span className={fileStyles.messageFileName}>
                                            {file.name}
                                        </span>
                                        {file.size && (
                                            <span className={fileStyles.messageFileSize}>
                                                ({formatFileSize(file.size)})
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    // Assistant messages are rendered as Markdown to support rich formatting.
                    <>
                        <ThinkingBlock blocks={thinkingBlocks} />
                        <MarkdownRenderer content={visibleContent} />
                        {/* The streaming cursor is shown only when the AI is actively "typing". */}
                        {isStreaming && <span className={styles.streamingCursor}></span>}
                    </>
                )}
            </div>
        </div>
    );
};

// --- React Performance Optimization ---
// By wrapping ChatMessage in React.memo, we prevent it from re-rendering
// unless its `message` prop has actually changed. This is critical for performance
// in a chat app, as it stops the entire list of messages from re-rendering
// on every single character received during a streaming response.
const MemoizedChatMessage = memo(ChatMessage);
export default MemoizedChatMessage;